import { useEffect, useState } from 'react';

type CookiePreferences = {
  necessary: boolean;
  functional: boolean;
  analytics: boolean;
  marketing: boolean;
};

const STORAGE_KEY = 'crmify_cookie_preferences';

const defaultPreferences: CookiePreferences = {
  necessary: true,
  functional: false,
  analytics: false,
  marketing: false,
};

function readStoredPreferences(): CookiePreferences | null {
  try {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    return stored ? (JSON.parse(stored) as CookiePreferences) : null;
  } catch (err) {
    console.warn('Unable to read cookie preferences:', err);
    return null;
  }
}

function writeStoredPreferences(prefs: CookiePreferences) {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
  } catch (err) {
    console.warn('Unable to store cookie preferences:', err);
  }
}

export function CookieConsent() {
  const initialPrefs = typeof window === 'undefined' ? null : readStoredPreferences();

  const [preferences, setPreferences] = useState<CookiePreferences | null>(initialPrefs);
  const [showBanner, setShowBanner] = useState(() => !initialPrefs);
  const [isModalOpen, setModalOpen] = useState(false);
  const [draft, setDraft] = useState<CookiePreferences>(preferences ?? defaultPreferences);

  useEffect(() => {
    if (!isModalOpen) return;
    setDraft(preferences ?? defaultPreferences);
  }, [isModalOpen, preferences]);

  function persist(prefs: CookiePreferences) {
    setPreferences(prefs);
    setShowBanner(false);
    writeStoredPreferences(prefs);
  }

  function handleAcceptAll() {
    persist({ necessary: true, functional: true, analytics: true, marketing: true });
  }

  function handleReject() {
    persist({ necessary: true, functional: false, analytics: false, marketing: false });
  }

  function handleToggle(category: keyof CookiePreferences, value: boolean) {
    setDraft((previous) => ({ ...previous, [category]: value }));
  }

  function handleSaveDraft() {
    persist({ ...draft, necessary: true });
    setModalOpen(false);
  }

  function openModal() {
    setModalOpen(true);
  }

  function closeModal() {
    setModalOpen(false);
  }

  const showSettingsButton = Boolean(preferences && !showBanner);

  return (
    <>
      {showBanner && (
        <div className="cookie-banner" role="dialog" aria-live="polite" aria-label="Cookie consent">
          <p>We use cookies to personalize content, analyze traffic, and improve your experience. Manage your preferences below.</p>
          <div className="cookie-banner__actions">
            <button className="cookie-action--accept" type="button" onClick={handleAcceptAll}>
              Accept all
            </button>
            <button className="cookie-action--reject" type="button" onClick={handleReject}>
              Reject non-essential
            </button>
            <button className="cookie-action--customize" type="button" onClick={openModal}>
              Customize
            </button>
          </div>
        </div>
      )}

      {isModalOpen && (
        <div className="cookie-modal" role="dialog" aria-modal="true" aria-labelledby="cookie-modal-title">
          <div className="cookie-modal__content" tabIndex={-1}>
            <h2 id="cookie-modal-title">Cookie preferences</h2>
            <p>Select which optional cookies you'd like to enable.</p>
            <div className="cookie-toggles">
              <div className="cookie-toggle">
                <div>
                  <strong>Necessary</strong>
                  <p>Required for core functionality.</p>
                </div>
                <input type="checkbox" checked disabled />
              </div>
              <label className="cookie-toggle">
                <div>
                  <strong>Functional</strong>
                  <p>Remember preferences and improve usability.</p>
                </div>
                <input
                  type="checkbox"
                  checked={draft.functional}
                  onChange={(event) => handleToggle('functional', event.target.checked)}
                />
              </label>
              <label className="cookie-toggle">
                <div>
                  <strong>Analytics</strong>
                  <p>Help us understand product usage.</p>
                </div>
                <input
                  type="checkbox"
                  checked={draft.analytics}
                  onChange={(event) => handleToggle('analytics', event.target.checked)}
                />
              </label>
              <label className="cookie-toggle">
                <div>
                  <strong>Marketing</strong>
                  <p>Show relevant offers and content.</p>
                </div>
                <input
                  type="checkbox"
                  checked={draft.marketing}
                  onChange={(event) => handleToggle('marketing', event.target.checked)}
                />
              </label>
            </div>
            <div className="cookie-modal__actions">
              <button className="cookie-action--save" type="button" onClick={handleSaveDraft}>
                Save preferences
              </button>
              <button className="cookie-action--cancel" type="button" onClick={closeModal}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {showSettingsButton && (
        <button className="cookie-settings-btn" type="button" onClick={openModal}>
          Cookie preferences
        </button>
      )}
    </>
  );
}
